import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsResourceAdmin, useIsManager } from './useUsers';

interface PerformanceReviewAccessOptions {
  /** Restrict to OWNER/ADMIN only, excluding managers (e.g. template settings). */
  adminOnly?: boolean;
}

/**
 * Page-level guard for performance-review routes. Resource admins (OWNER/ADMIN,
 * excluding BILLING) always pass; managers pass unless the page is admin-only.
 * Everyone else is redirected to the dashboard.
 *
 * Returns whether the caller may render its content — false while the session
 * role resolves, so pages render nothing instead of flashing content.
 */
export const usePerformanceReviewAccess = (
  { adminOnly = false }: PerformanceReviewAccessOptions = {},
): boolean => {
  const navigate = useNavigate();
  const { isAdmin, isLoading: adminLoading } = useIsResourceAdmin();
  const { isManager, isLoading: managerLoading } = useIsManager();

  const isLoading = adminLoading || managerLoading;
  const canView = isAdmin || (!adminOnly && isManager);

  useEffect(() => {
    if (!isLoading && !canView) {
      navigate('/dashboard', { replace: true });
    }
  }, [isLoading, canView, navigate]);

  return !isLoading && canView;
};
